import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Plus,
  GripVertical,
  Trash2,
  Video,
  ChevronDown,
  ChevronRight,
  Play,
  Upload,
  Settings,
  Eye,
  Send
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const TrainingEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const isNew = !id || id === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [training, setTraining] = useState({
    title: '',
    description: '',
    thumbnail_url: '',
    is_sequential: true,
    status: 'draft',
    modules: []
  });
  const [expandedModules, setExpandedModules] = useState({});
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showYouTubeDialog, setShowYouTubeDialog] = useState(false);
  const [youtubeUploading, setYoutubeUploading] = useState(false);
  const [selectedMediaForYouTube, setSelectedMediaForYouTube] = useState(null);

  useEffect(() => {
    if (!isNew) {
      fetchTraining();
    }
    fetchMedia();
  }, [id]);

  const fetchTraining = async () => {
    try {
      const response = await api.get(`/trainings/${id}`);
      setTraining(response.data);
      // Expand all modules by default
      const expanded = {};
      response.data.modules.forEach(m => {
        expanded[m.id] = true;
      });
      setExpandedModules(expanded);
    } catch (error) {
      toast.error('Formation introuvable');
      navigate('/trainings');
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await api.get('/media');
      setMediaLibrary(response.data);
    } catch (error) {
      console.error('Failed to fetch media:', error);
    }
  };

  const saveTraining = async () => {
    if (!training.title.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }
    
    setSaving(true);
    try {
      if (isNew) {
        const response = await api.post('/trainings', training);
        toast.success('Formation créée !');
        navigate(`/trainings/${response.data.id}`);
      } else {
        await api.put(`/trainings/${id}`, training);
        toast.success('Formation sauvegardée !');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      await api.put(`/trainings/${id}/status?status=${newStatus}`);
      setTraining({ ...training, status: newStatus });
      toast.success('Statut mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const addModule = () => {
    const newModule = {
      id: `temp-${Date.now()}`,
      title: 'Nouveau module',
      description: '',
      order: training.modules.length,
      lessons: [],
      status: 'draft'
    };
    setTraining({
      ...training,
      modules: [...training.modules, newModule]
    });
    setExpandedModules({ ...expandedModules, [newModule.id]: true });
  };

  const updateModule = (moduleId, updates) => {
    setTraining({
      ...training,
      modules: training.modules.map(m =>
        m.id === moduleId ? { ...m, ...updates } : m
      )
    });
  };

  const deleteModule = (moduleId) => {
    if (!window.confirm('Supprimer ce module et toutes ses leçons ?')) return;
    setTraining({
      ...training,
      modules: training.modules.filter(m => m.id !== moduleId)
    });
  };

  const addLesson = (moduleId) => {
    const newLesson = {
      id: `temp-${Date.now()}`,
      title: 'Nouvelle leçon',
      description: '',
      video_id: null,
      youtube_url: null,
      duration: 0,
      order: 0,
      status: 'draft'
    };
    setTraining({
      ...training,
      modules: training.modules.map(m =>
        m.id === moduleId
          ? { ...m, lessons: [...(m.lessons || []), newLesson] }
          : m
      )
    });
  };

  const updateLesson = (moduleId, lessonId, updates) => {
    setTraining({
      ...training,
      modules: training.modules.map(m =>
        m.id === moduleId
          ? {
              ...m,
              lessons: m.lessons.map(l =>
                l.id === lessonId ? { ...l, ...updates } : l
              )
            }
          : m
      )
    });
  };

  const deleteLesson = (moduleId, lessonId) => {
    setTraining({
      ...training,
      modules: training.modules.map(m =>
        m.id === moduleId
          ? { ...m, lessons: m.lessons.filter(l => l.id !== lessonId) }
          : m
      )
    });
  };

  const selectVideoForLesson = (moduleId, lessonId) => {
    setSelectedLesson({ moduleId, lessonId });
    setShowMediaPicker(true);
  };

  const assignVideoToLesson = (media) => {
    if (selectedLesson) {
      updateLesson(selectedLesson.moduleId, selectedLesson.lessonId, {
        video_id: media.id
      });
      setShowMediaPicker(false);
      setSelectedLesson(null);
      toast.success('Vidéo assignée');
    }
  };

  const uploadToYouTube = async () => {
    if (!selectedMediaForYouTube) return;
    
    setYoutubeUploading(true);
    try {
      const response = await api.post('/youtube/upload', {
        media_id: selectedMediaForYouTube.id,
        title: selectedMediaForYouTube.original_filename || 'Vidéo de formation',
        description: 'Formation Valnexo Academy',
        privacy: 'unlisted'
      });
      
      // Update local media state
      setMediaLibrary(mediaLibrary.map(m =>
        m.id === selectedMediaForYouTube.id
          ? { ...m, youtube_url: response.data.youtube_url, youtube_status: 'uploaded' }
          : m
      ));
      
      toast.success('Vidéo publiée sur YouTube !');
      setShowYouTubeDialog(false);
      setSelectedMediaForYouTube(null);
    } catch (error) {
      toast.error('Erreur lors de la publication');
    } finally {
      setYoutubeUploading(false);
    }
  };

  const getVideoInfo = (videoId) => {
    return mediaLibrary.find(m => m.id === videoId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/trainings')}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="font-semibold text-slate-900 font-['Outfit']">
                {isNew ? 'Nouvelle formation' : 'Modifier la formation'}
              </h1>
              <p className="text-sm text-slate-500">
                {training.modules.length} modules • {training.modules.reduce((sum, m) => sum + (m.lessons?.length || 0), 0)} leçons
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {!isNew && (
              <Select value={training.status} onValueChange={updateStatus}>
                <SelectTrigger className="w-40" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="ready">Prêt</SelectItem>
                  <SelectItem value="published">Publié</SelectItem>
                  <SelectItem value="archived">Archivé</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button
              data-testid="save-training-btn"
              onClick={saveTraining}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Sauvegarder
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Informations générales</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre de la formation</Label>
                  <Input
                    id="title"
                    data-testid="training-title-input"
                    value={training.title}
                    onChange={(e) => setTraining({ ...training, title: e.target.value })}
                    placeholder="Ex: Onboarding Commercial"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    data-testid="training-description-input"
                    value={training.description}
                    onChange={(e) => setTraining({ ...training, description: e.target.value })}
                    placeholder="Décrivez l'objectif de cette formation..."
                    rows={3}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Progression séquentielle</Label>
                    <p className="text-sm text-slate-500">
                      Les apprenants doivent suivre l'ordre des leçons
                    </p>
                  </div>
                  <Switch
                    checked={training.is_sequential}
                    onCheckedChange={(checked) => setTraining({ ...training, is_sequential: checked })}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Modules */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Modules</h2>
                <Button
                  data-testid="add-module-btn"
                  onClick={addModule}
                  variant="outline"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un module
                </Button>
              </div>

              {training.modules.length === 0 ? (
                <Card className="border border-dashed border-slate-300">
                  <CardContent className="p-8 text-center">
                    <p className="text-slate-500 mb-4">
                      Aucun module. Créez votre premier module pour structurer la formation.
                    </p>
                    <Button onClick={addModule} variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Créer un module
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {training.modules.map((module, moduleIndex) => (
                    <Card key={module.id} className="border border-slate-200" data-testid={`module-${module.id}`}>
                      <Collapsible
                        open={expandedModules[module.id]}
                        onOpenChange={(open) => setExpandedModules({ ...expandedModules, [module.id]: open })}
                      >
                        <CollapsibleTrigger asChild>
                          <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                            <div className="flex items-center gap-3">
                              <GripVertical className="w-5 h-5 text-slate-400" />
                              <div className="flex-1">
                                <Input
                                  value={module.title}
                                  onChange={(e) => updateModule(module.id, { title: e.target.value })}
                                  onClick={(e) => e.stopPropagation()}
                                  className="font-medium border-transparent hover:border-slate-200 focus:border-blue-500"
                                  placeholder="Nom du module"
                                />
                              </div>
                              <span className="text-sm text-slate-500">
                                {module.lessons?.length || 0} leçons
                              </span>
                              {expandedModules[module.id] ? (
                                <ChevronDown className="w-5 h-5 text-slate-400" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-slate-400" />
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteModule(module.id);
                                }}
                                className="p-2 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </CardHeader>
                        </CollapsibleTrigger>
                        
                        <CollapsibleContent>
                          <CardContent className="pt-0 space-y-3">
                            <Textarea
                              value={module.description}
                              onChange={(e) => updateModule(module.id, { description: e.target.value })}
                              placeholder="Description du module (optionnel)"
                              rows={2}
                              className="text-sm"
                            />
                            
                            {/* Lessons */}
                            <div className="space-y-2 pl-4 border-l-2 border-slate-200">
                              {(module.lessons || []).map((lesson, lessonIndex) => {
                                const videoInfo = lesson.video_id ? getVideoInfo(lesson.video_id) : null;
                                return (
                                  <div
                                    key={lesson.id}
                                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg group"
                                    data-testid={`lesson-${lesson.id}`}
                                  >
                                    <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-sm font-medium text-slate-500">
                                      {lessonIndex + 1}
                                    </div>
                                    <Input
                                      value={lesson.title}
                                      onChange={(e) => updateLesson(module.id, lesson.id, { title: e.target.value })}
                                      className="flex-1 bg-white"
                                      placeholder="Titre de la leçon"
                                    />
                                    {videoInfo ? (
                                      <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm">
                                        <Video className="w-4 h-4" />
                                        <span className="max-w-[100px] truncate">{videoInfo.original_filename}</span>
                                        {videoInfo.youtube_url && (
                                          <a 
                                            href={videoInfo.youtube_url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline"
                                          >
                                            YT
                                          </a>
                                        )}
                                      </div>
                                    ) : (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => selectVideoForLesson(module.id, lesson.id)}
                                      >
                                        <Video className="w-4 h-4 mr-1" />
                                        Vidéo
                                      </Button>
                                    )}
                                    <button
                                      onClick={() => deleteLesson(module.id, lesson.id)}
                                      className="p-1.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                );
                              })}
                              
                              <button
                                onClick={() => addLesson(module.id)}
                                className="w-full py-2 text-sm text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                                data-testid={`add-lesson-btn-${module.id}`}
                              >
                                <Plus className="w-4 h-4" />
                                Ajouter une leçon
                              </button>
                            </div>
                          </CardContent>
                        </CollapsibleContent>
                      </Collapsible>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="text-base">Actions rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/studio')}
                >
                  <Video className="w-4 h-4 mr-2" />
                  Enregistrer une vidéo
                </Button>
                {!isNew && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => navigate(`/learn/${id}`)}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Prévisualiser
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Media Library Quick Access */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="text-base">Vidéos disponibles</CardTitle>
              </CardHeader>
              <CardContent>
                {mediaLibrary.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Aucune vidéo. Enregistrez depuis le studio.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {mediaLibrary.slice(0, 5).map((media) => (
                      <div
                        key={media.id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 group"
                      >
                        <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                          <Video className="w-5 h-5 text-slate-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {media.original_filename}
                          </p>
                          <p className="text-xs text-slate-500">
                            {media.youtube_status === 'uploaded' ? (
                              <span className="text-emerald-600">Sur YouTube</span>
                            ) : (
                              'Non publié'
                            )}
                          </p>
                        </div>
                        {media.youtube_status !== 'uploaded' && (
                          <button
                            onClick={() => {
                              setSelectedMediaForYouTube(media);
                              setShowYouTubeDialog(true);
                            }}
                            className="p-1.5 rounded hover:bg-blue-100 text-slate-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-all"
                            title="Publier sur YouTube"
                          >
                            <Upload className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Sélectionner une vidéo</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {mediaLibrary.length === 0 ? (
              <div className="text-center py-8">
                <Video className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune vidéo disponible</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    setShowMediaPicker(false);
                    navigate('/studio');
                  }}
                >
                  Aller au studio
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {mediaLibrary.map((media) => (
                  <div
                    key={media.id}
                    onClick={() => assignVideoToLesson(media)}
                    className="p-4 border border-slate-200 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
                  >
                    <div className="aspect-video bg-slate-100 rounded mb-3 flex items-center justify-center">
                      <Play className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="font-medium text-slate-900 truncate">
                      {media.original_filename}
                    </p>
                    <p className="text-sm text-slate-500">
                      {new Date(media.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* YouTube Upload Dialog */}
      <Dialog open={showYouTubeDialog} onOpenChange={setShowYouTubeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publier sur YouTube</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              La vidéo <strong>{selectedMediaForYouTube?.original_filename}</strong> sera publiée en mode <strong>non répertorié</strong>.
            </p>
            <p className="text-sm text-slate-500">
              Note: Cette fonctionnalité est simulée pour le MVP. En production, la vidéo sera réellement uploadée sur YouTube.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowYouTubeDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={uploadToYouTube}
              disabled={youtubeUploading}
              className="bg-red-600 hover:bg-red-700"
            >
              {youtubeUploading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Publier sur YouTube
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainingEditorPage;
